import { useFetch, fmtDate } from "@/lib/shieldx";
import { PageHeader, Panel, Loader, Stat } from "@/components/shieldx/Primitives";
import { StatusBadge, DemoTag } from "@/components/shieldx/Badges";
import { Gauge, ShieldCheck, Lightbulb } from "lucide-react";

const RECS = [
  "Enable MFA for all organization owners and analysts.",
  "Move pending integrations through admin review before connecting.",
  "Review AI agents at HIGH or CRITICAL risk and tighten their authorization scope.",
  "Validate the latest recovery points for production databases.",
];

function Ring({ value }) {
  const r = 52, c = 2 * Math.PI * r, off = c - (value / 100) * c;
  const color = value >= 85 ? "#10B981" : value >= 70 ? "#2563EB" : value >= 55 ? "#F59E0B" : "#EF4444";
  return (
    <svg width="140" height="140" viewBox="0 0 140 140">
      <circle cx="70" cy="70" r={r} fill="none" stroke="#1e293b" strokeWidth="12" />
      <circle cx="70" cy="70" r={r} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 70 70)" />
      <text x="70" y="66" textAnchor="middle" className="font-display" fontSize="28" fontWeight="700" fill="#f8fafc">{value}</text>
      <text x="70" y="86" textAnchor="middle" fontSize="10" fill="#64748b" fontFamily="JetBrains Mono">SECURITY SCORE</text>
    </svg>
  );
}

export default function Dashboard() {
  const cc = useFetch("/dashboard/command-center");
  const orgs = useFetch("/organizations");
  const integrations = useFetch("/integrations");
  const incidents = useFetch("/incidents");
  if (cc.loading || orgs.loading || integrations.loading || incidents.loading) return <Loader label="Loading organization dashboard" />;
  const k = cc.data.kpis;
  const org = (orgs.data || [])[0] || {};
  const score = org.security_score || 75;
  const recentIncidents = (incidents.data || []).slice(0, 6);

  return (
    <div>
      <PageHeader icon={Gauge} title="Organization Dashboard"
        subtitle={`Security posture for ${org.name || "your organization"}.`} actions={<DemoTag />} />

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Security Score" desc="Composite protection rating">
          <div className="flex items-center gap-6">
            <Ring value={score} />
            <div>
              <StatusBadge value={org.protection_status || "MONITORING"} />
              <p className="mt-3 text-sm text-slate-400">Tier: <span className="text-slate-200">{org.tier || "Standard"}</span></p>
              <p className="mt-1 text-sm text-slate-400">MFA required: <span className="text-slate-200">{org.mfa_required ? "Yes" : "No"}</span></p>
            </div>
          </div>
        </Panel>
        <div className="grid grid-cols-2 gap-4 lg:col-span-2">
          <Stat label="Protected Assets" value={`${k.protected_assets}/${k.total_assets}`} tone="blue" />
          <Stat label="Active Threats" value={k.active_threats} tone="amber" />
          <Stat label="Blocked Attacks" value={k.blocked_attacks} tone="emerald" />
          <Stat label="AI Threats" value={k.ai_threats} tone="cyan" />
          <Stat label="Critical Incidents" value={k.critical_incidents} tone="red" />
          <Stat label="Integration Health" value={`${k.integration_health}%`} tone="emerald" />
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="Recent Events" desc="Latest incidents across your estate">
          <div className="space-y-2">
            {recentIncidents.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800/70 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-200">{i.title}</p>
                  <p className="font-mono text-[11px] text-slate-500">{i.incident_id} · {fmtDate(i.created_at)}</p>
                </div>
                <StatusBadge value={i.state} />
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Security Recommendations" desc="Prioritized hardening actions">
          <ul className="space-y-3">
            {RECS.map((r) => (
              <li key={r} className="flex items-start gap-3 rounded-lg border border-slate-800/70 px-3 py-2.5 text-sm text-slate-300">
                <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" /> {r}
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
